import React from "react";
import { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";
import type { DeliveryPreferencesStepKey } from "./deliveryPreferencesStore";
import DeliveryTimeStep from "./steps/DeliveryTimeStep";
import DeliveryContactStep from "./steps/DeliveryContactStep";
import BuyerDetailsStep from "./steps/BuyerDetailsStep";
import EmailVerificationStep from "./steps/EmailVerificationStep";
import AccountSetupStep from "./steps/AccountSetupStep";
import CompletionStep from "./steps/CompletionStep";

export type StepConfig = {
  key: DeliveryPreferencesStepKey;
  component: React.ComponentType;
  schema: z.ZodType;
  next: DeliveryPreferencesStepKey | null;
  label: string;
};

const emailField = z
  .string()
  .min(1, "Email is required")
  .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Please enter a valid email address");

const phoneField = z
  .string()
  .min(1, "Phone number is required")
  .refine((val) => isValidPhoneNumber(val, "GB"), {
    message: "Please enter a valid phone number",
  });

export const deliveryTimeSchema = z.object({
  deliveryTime: z.enum(["AM", "EARLY_PM", "FLEXIBLE"], {
    message: "Select a delivery time preference",
  }),
});

export const deliveryContactSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: emailField,
  phone: phoneField,
  additionalContacts: z.array(
    z.object({
      name: z.string().min(1, "Name is required"),
      email: emailField,
      phone: phoneField,
    }),
  ),
});

export const buyerDetailsSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: emailField,
  phone: phoneField,
});

export const emailVerificationSchema = z.object({
  otp: z.string().length(6, "Please enter all 6 digits"),
});

export const accountSetupSchema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
    acceptedTerms: z.boolean().refine((v) => v === true, {
      message: "You must accept the Terms & Conditions",
    }),
    supplierAcknowledgement: z.boolean().refine((v) => v === true, {
      message: "You must confirm your association with this business",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export const completionSchema = z.object({});

export const stepRegistry: StepConfig[] = [
  {
    key: "deliveryTime",
    component: DeliveryTimeStep,
    schema: deliveryTimeSchema,
    next: "deliveryContact",
    label: "Delivery Time",
  },
  {
    key: "deliveryContact",
    component: DeliveryContactStep,
    schema: deliveryContactSchema,
    next: "buyerDetails",
    label: "Delivery Contact",
  },
  {
    key: "buyerDetails",
    component: BuyerDetailsStep,
    schema: buyerDetailsSchema,
    next: "emailVerification",
    label: "Buyer Details",
  },
  {
    key: "emailVerification",
    component: EmailVerificationStep,
    schema: emailVerificationSchema,
    next: "accountSetup",
    label: "Email Verification",
  },
  {
    key: "accountSetup",
    component: AccountSetupStep,
    schema: accountSetupSchema,
    next: "completion",
    label: "Account Setup",
  },
  {
    key: "completion",
    component: CompletionStep,
    schema: completionSchema,
    next: null,
    label: "Complete",
  },
];
